import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import GoogleButton from "@/components/GoogleButton";
import useSeo from "@/components/useSeo";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [twoFA, setTwoFA] = useState(false);
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  useSeo({
    title: "Log in · SphereHyx",
    description: "Log in to your SphereHyx account to manage your sports club, teams, athletes and payments.",
    canonical: "https://spherehyx.com/login",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, totp: totp || undefined });
      if (data?.two_factor_required) {
        setTwoFA(true);
        toast.message("Enter the 6-digit code from your authenticator app");
        setLoading(false);
        return;
      }
      if (data?.token) localStorage.setItem("cs_token", data.token);
      const u = await refresh();
      navigate(u?.role === "parent" ? "/parent" : u?.role === "player" ? "/portal" : u?.role === "admin" ? "/platform" : "/app");
    } catch (err) {
      toast.error(apiErr(err, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/38116744/pexels-photo-38116744.jpeg?auto=compress&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/60 to-transparent" />
        <div className="absolute bottom-12 left-12">
          <Logo size={38} />
          <p className="mt-4 font-heading font-bold text-3xl max-w-sm tracking-tight">Welcome back, Coach.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo size={34} /></div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Log in to SphereHyx</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your club, teams and athletes.</p>

          <GoogleButton testid="google-login-btn" />
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="login-email-input" disabled={twoFA} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="login-password-input" disabled={twoFA} />
            </div>
            {twoFA && (
              <div>
                <Label htmlFor="totp">Authenticator code</Label>
                <Input id="totp" inputMode="numeric" autoFocus placeholder="6-digit code" value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))} className="mt-1.5 h-11 tracking-widest" data-testid="login-totp-input" />
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11 brand-gradient text-white border-0 hover:opacity-90" data-testid="login-submit-btn">
              {loading ? "Please wait..." : twoFA ? "Verify code" : "Log in"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            No account? <Link to="/register" className="text-primary font-medium hover:underline">Create your club</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
